import { FormValues } from './features/project/form';

const AUTH_FAILURE_PATTERNS = [
  'not autorised',
  'not authorised',
  'not authorized',
  'unauthorised',
  'unauthorized',
  'login required',
];

const PROJECT_FORM_DRAFT_KEY = 'FORMIC_PROJECT_FORM_DRAFT';

interface ProjectFormDraft {
  url: string;
  values: FormValues;
  expiresAt: number;
}

export const isAuthenticationError = (error: unknown): boolean => {
  if (error instanceof Error && error.name === 'LoginRequiredError') return true;

  const message = error instanceof Error ? error.message : String(error ?? '');
  const normalizedMessage = message.toLowerCase();

  return AUTH_FAILURE_PATTERNS.some((pattern) => normalizedMessage.includes(pattern));
};

export const redirectToLogin = async (): Promise<void> => {
  const { getAuthService } = await import('./authService');
  const authService = await getAuthService();
  await (authService.login as (returnUrl?: string) => Promise<void>)(window.location.href);
};

export const saveProjectFormDraft = (
  values: FormValues,
  returnUrl: string = window.location.href,
): void => {
  try {
    const draft: ProjectFormDraft = {
      url: returnUrl,
      values,
      expiresAt: Date.now() + 60 * 60 * 1000,
    };

    sessionStorage.setItem(PROJECT_FORM_DRAFT_KEY, JSON.stringify(draft));
  } catch (error) {
    console.warn('Unable to save form draft before reauthentication.', error);
  }
};

export const loadProjectFormDraft = (): FormValues | undefined => {
  try {
    const rawDraft = sessionStorage.getItem(PROJECT_FORM_DRAFT_KEY);
    if (!rawDraft) return undefined;

    const draft = JSON.parse(rawDraft) as ProjectFormDraft;
    if (draft.expiresAt < Date.now()) {
      clearProjectFormDraft();
      return undefined;
    }

    return draft.url === window.location.href ? draft.values : undefined;
  } catch (error) {
    console.warn('Unable to restore form draft after reauthentication.', error);
    clearProjectFormDraft();
    return undefined;
  }
};

export const clearProjectFormDraft = (): void => {
  sessionStorage.removeItem(PROJECT_FORM_DRAFT_KEY);
};
